import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { X, Minus, Plus, ShoppingCart, Package } from "lucide-react";
import { useApp } from "../context/AppContext";
import { formatMoney, applyPriceTiers, isVideoUrl, isBundleProduct, getBundleTier, bundleOptions } from "../lib/api";
import { toast } from "sonner";

const QuickAddModal = ({ product, onClose }) => {
  const { lang, currency, addToCart } = useApp();
  const navigate = useNavigate();
  const variants = product.variants || [];
  const [variant, setVariant] = useState(variants[0] || null);
  const [qty, setQty] = useState(1);
  const bundleMode = isBundleProduct(product);
  const options = bundleMode ? bundleOptions(product) : [];
  const [bundleQty, setBundleQty] = useState(1);

  const name = product[`name_${lang}`] || product.name_en;
  const img = variant?.image || product.images?.[0] || "";
  const tiers = (product.price_tiers || []).slice().sort((a, b) => a.min_qty - b.min_qty);
  const tiered = applyPriceTiers(product, qty);
  const tierActive = !bundleMode && tiered.tier !== null;
  const activeBundle = bundleMode ? getBundleTier(product, bundleQty) || { min_qty: 1, price_idr: product.price_idr, price_usd: product.price_usd } : null;
  const maxPkgs = bundleMode ? Math.max(1, Math.floor((product.stock || 0) / (bundleQty || 1))) : (product.stock || 99);

  const add = (goCheckout) => {
    const productWithVariant = variant
      ? { ...product, name_id: `${product.name_id} - ${variant.name}`, name_en: `${product.name_en} - ${variant.name}`, images: [img] }
      : product;
    addToCart(productWithVariant, qty, bundleMode ? bundleQty : 0);
    if (goCheckout) {
      navigate("/checkout");
      return;
    }
    onClose();
    toast.success(lang === "id" ? `${name} ditambahkan ke keranjang` : `${name} added to cart`, {
      action: {
        label: "Checkout",
        onClick: () => navigate("/checkout"),
      },
    });
  };

  return createPortal(
    <div className="fixed inset-0 bg-foreground/50 z-50 flex items-end sm:items-center justify-center sm:p-4" onClick={onClose} data-testid="quickadd-overlay">
      <div
        className="bg-ivory w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl p-5 sm:p-6 max-h-[85vh] overflow-y-auto shadow-xl animate-fade-up"
        onClick={(e) => e.stopPropagation()}
        data-testid="quickadd-modal"
      >
        <div className="flex items-start gap-4 mb-4">
          <div className="w-20 h-20 bg-white rounded-xl overflow-hidden border border-border/60 shrink-0">
            {img && (
              isVideoUrl(img) ? (
                <video
                  src={img}
                  muted
                  playsInline
                  preload="metadata"
                  onLoadedMetadata={(e) => { e.target.currentTime = 0.1; }}
                  className="w-full h-full object-cover"
                />
              ) : (
                <img src={img} alt={name} className="w-full h-full object-contain" />
              )
            )}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-serif text-xl leading-tight text-foreground">{name}</h3>
            {bundleMode && activeBundle ? (
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-holy-red font-semibold" data-testid="quickadd-price">
                  {formatMoney(activeBundle.price_idr, activeBundle.price_usd, currency)}
                </span>
                <span className="text-xs text-muted-foreground">
                  {bundleQty === 1 ? "/pc" : `/${lang === "id" ? "paket" : "pack"} ${bundleQty} pcs`}
                </span>
              </div>
            ) : (
              <div className="flex items-baseline gap-2 mt-1">
                <span className="text-holy-red font-semibold" data-testid="quickadd-price">
                  {formatMoney(tiered.price_idr, tiered.price_usd, currency)}
                </span>
                <span className="text-xs text-muted-foreground">/pc</span>
                {tierActive && (
                  <span className="text-xs text-muted-foreground line-through">
                    {formatMoney(product.price_idr, product.price_usd, currency)}
                  </span>
                )}
              </div>
            )}
            {tierActive && (
              <div className="text-[11px] text-holy-green font-semibold mt-0.5">
                {lang === "id" ? `Harga grosir ${tiered.tier.min_qty}+ pcs` : `Wholesale ${tiered.tier.min_qty}+ pcs`}
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 text-muted-foreground hover:text-holy-red" data-testid="quickadd-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {variants.length > 0 && (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {lang === "id" ? "Pilih Warna" : "Choose Color"}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="quickadd-variants">
              {variants.map((v, i) => {
                const sel = variant?.name === v.name;
                return (
                  <button
                    key={i}
                    onClick={() => setVariant(v)}
                    data-testid={`quickadd-variant-${v.name}`}
                    title={v.name}
                    className={`flex items-center gap-2 px-3 py-1.5 rounded-full border text-xs transition-all ${
                      sel ? "border-holy-red bg-holy-red/5 text-holy-red font-semibold" : "border-border bg-white hover:border-holy-red/50"
                    }`}
                  >
                    <span className="w-4 h-4 rounded-full border border-border/60 shrink-0" style={{ backgroundColor: v.color_hex || "#fff" }} />
                    {v.name}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {bundleMode ? (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1">
              <Package className="w-3.5 h-3.5" /> {lang === "id" ? "Pilih Satuan / Paket" : "Choose Single / Pack"}
            </div>
            <div className="flex flex-wrap gap-2" data-testid="quickadd-bundles">
              {options.map((b) => {
                const sel = b.min_qty === bundleQty;
                return (
                  <button
                    key={b.min_qty}
                    onClick={() => { setBundleQty(b.min_qty); setQty(1); }}
                    data-testid={`quickadd-bundle-${b.min_qty}`}
                    className={`px-3 py-2 rounded-xl border text-center transition-all ${
                      sel ? "border-holy-red bg-holy-red/5 text-holy-red" : "border-border bg-white hover:border-holy-red/50"
                    }`}
                  >
                    <span className="block text-sm font-semibold">{b.min_qty === 1 ? "1 pc" : `${b.min_qty} pcs`}</span>
                    <span className="block text-[11px]">{formatMoney(b.price_idr, b.price_usd, currency)}</span>
                  </button>
                );
              })}
            </div>
            <div className="flex items-center gap-3 mt-3">
              <span className="text-[10px] uppercase tracking-widest text-muted-foreground">
                {bundleQty === 1 ? (lang === "id" ? "Jumlah" : "Qty") : (lang === "id" ? "Jumlah Paket" : "Packs")}
              </span>
              <div className="flex items-center border border-border rounded-full overflow-hidden bg-white">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 hover:bg-cream" data-testid="quickadd-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
                <span className="px-4 text-sm font-semibold" data-testid="quickadd-qty">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(maxPkgs, q + 1))} className="px-3 py-2 hover:bg-cream" data-testid="quickadd-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-sm font-semibold text-holy-red" data-testid="quickadd-total">
                = {formatMoney((activeBundle?.price_idr || 0) * qty, (activeBundle?.price_usd || 0) * qty, currency)}
              </div>
            </div>
            <div className="text-[11px] text-muted-foreground mt-2">
              {lang === "id"
                ? "Bisa beli satuan (1 pc) atau per paket untuk harga lebih hemat."
                : "Buy single (1 pc) or in packs for better value."}
            </div>
          </div>
        ) : (
          <div className="mb-4">
            <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
              {lang === "id" ? "Jumlah" : "Quantity"}
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center border border-border rounded-full overflow-hidden bg-white">
                <button onClick={() => setQty((q) => Math.max(1, q - 1))} className="px-3 py-2 hover:bg-cream" data-testid="quickadd-qty-minus"><Minus className="w-3.5 h-3.5" /></button>
                <span className="px-4 text-sm font-semibold" data-testid="quickadd-qty">{qty}</span>
                <button onClick={() => setQty((q) => Math.min(product.stock || 99, q + 1))} className="px-3 py-2 hover:bg-cream" data-testid="quickadd-qty-plus"><Plus className="w-3.5 h-3.5" /></button>
              </div>
              <div className="text-sm font-semibold text-holy-red" data-testid="quickadd-total">
                = {formatMoney(tiered.price_idr * qty, tiered.price_usd * qty, currency)}
              </div>
            </div>
            {tiers.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {tiers.map((t2) => (
                  <button
                    key={t2.min_qty}
                    onClick={() => setQty(Math.min(product.stock || 99, t2.min_qty))}
                    data-testid={`quickadd-tier-${t2.min_qty}`}
                    className={`text-[11px] px-2.5 py-1 rounded-full border transition-all ${
                      qty >= t2.min_qty ? "border-holy-green bg-holy-green/10 text-holy-green font-semibold" : "border-border bg-white text-muted-foreground hover:border-holy-red"
                    }`}
                  >
                    {t2.min_qty}+ → {formatMoney(t2.price_idr, t2.price_usd, currency)}/pc
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="flex gap-2">
          <button
            data-testid="quickadd-add-btn"
            onClick={() => add(false)}
            className="flex-1 inline-flex items-center justify-center gap-2 bg-holy-red text-ivory rounded-full py-3 text-sm font-semibold hover:bg-holy-red-dark transition-all"
          >
            <ShoppingCart className="w-4 h-4" />
            {lang === "id" ? "Tambah ke Keranjang" : "Add to Cart"}
          </button>
          <button
            data-testid="quickadd-buy-btn"
            onClick={() => add(true)}
            className="flex-1 border border-holy-red text-holy-red rounded-full py-3 text-sm font-semibold hover:bg-holy-red/5 transition-all"
          >
            {lang === "id" ? "Beli Sekarang" : "Buy Now"}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default QuickAddModal;
